import { MatSelectCountryModule } from '@angular-material-extensions/select-country';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Optional, Provider } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AceModule } from 'ngx-ace-wrapper';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { SharedModule } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipsModule } from 'primeng/chips';
import { DropdownModule } from 'primeng/dropdown';
import { CheckIcon } from 'primeng/icons/check';
import { ChevronDownIcon } from 'primeng/icons/chevrondown';
import { SearchIcon } from 'primeng/icons/search';
import { TimesIcon } from 'primeng/icons/times';
import { TimesCircleIcon } from 'primeng/icons/timescircle';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { OverlayModule } from 'primeng/overlay';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RippleModule } from 'primeng/ripple';
import { ScrollerModule } from 'primeng/scroller';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { AddRolesDialogComponent } from './components/add-roles-dialog/add-roles-dialog.component';
import { ApiKeyDialogComponent } from './components/api-key-dialog/apikey-dialog.component';
import { ApiKeyComponent } from './components/api-key/api-key.component';
import { ApiKeysComponent } from './components/api-keys/api-keys.component';
import { BillingInvoiceListComponent } from './components/billing-invoice-list/billing-invoice-list.component';
import { BillingInvoiceReceiverEditAddressDialogComponent } from './components/billing-invoice-receiver-edit-address-dialog/billing-invoice-receiver-edit-address.component';
import { BillingInvoiceReceiverEditEmailDialogComponent } from './components/billing-invoice-receiver-edit-email-dialog/billing-invoice-receiver-edit-email.component';
import { BillingInvoiceReceiverViewComponent } from './components/billing-invoice-receiver-view/billing-invoice-receiver-view.component';
import { BillingLicenceViewComponent } from './components/billing-licence-view/billing-licence-view.component';
import { BillingPaymentMethodViewComponent } from './components/billing-payment-method-view/billing-payment-method-view.component';
import { BillingComponent } from './components/billing/billing.component';
import { CommandButtonComponent } from './components/command-button/command-button.component';
import { CommandConfirmDialogComponent } from './components/command-confirm-dialog/command-confirm-dialog.component';
import { CompanyDialogComponent } from './components/company-dialog/company-dialog.component';
import { CompanyViewComponent } from './components/company-view/company-view.component';
import { StateService } from './components/componentState/state-service';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { ConfirmEmailDeletionDialogComponent } from './components/confirm-email-deletion-dialog/confirm-email-deletion-dialog.component';
import { ConfirmInvoiceAddressDeletionDialogComponent } from './components/confirm-invoice-address-deletion/confirm-invoice-address-deletion.component';
import { ConfirmLicenceRequestsDeletionDialogComponent } from './components/confirm-licence-request-deletion/confirm-licence-request-deletion.component';
import { NxConfirmDialogComponent } from './components/confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from './components/confirmDialog/confirmDialogService';
import { DefaultBoxComponent } from './components/default-box/default-box.component';
import { DefaultButtonComponent } from './components/default-button/default-button.component';
import { DeveloperPageComponent } from './components/developer-page/developer-page.component';
import { EditEmailDialogComponent } from './components/edit-email-dialog/edit-email-dialog.component';
import { EditInvoiceAddressDialogComponent } from './components/edit-invoice-address-dialog /edit-invoice-address-dialog.component';
import { EditDialogComponent, EditDialogDynamicContent } from './components/editDialog/editDialogComponent';
import { EditDialogService } from './components/editDialog/editDialogService';
import { EmptyBoxComponent } from './components/empty-box/empty-box.component';
import { EventsComponent } from './components/events/events.component';
import { NxFilterDateSelect } from './components/filter-date-select/nx-filter-date-select';
import { NxFilterDropdown, NxFilterDropdownItem } from './components/filter-dropdown/nx-filter-dropdown';
import { NxFilterMultiSelect, NxFilterMultiSelectItem } from './components/filter-multiselect/nx-filter-multiselect';
import { NxFilterNumberSelect } from './components/filter-numberselect/nx-filter-numberselect';
import { FormVariantComponent } from './components/form-variant/form-variant.component';
import { FormsListComponent } from './components/forms-list/forms-list.component';
import { GroupDialogComponent } from './components/group-dialog/group-dialog.component';
import { GroupListComponent } from './components/group-list/group-list.component';
import { GroupViewComponent } from './components/group-view/group-view.component';
import { HeadlineBoxComponent } from './components/headline-box/headline-box.component';
import { HttpLogsComponent } from './components/http-logs/http-logs.component';
import { IncrementDecrementInputComponent } from './components/increment-decrement-input/increment-decrement-input.component';
import { InvoicesComponent } from './components/invoices/invoices.component';
import { LayoutContentBodyComponent } from './components/layout-content-body/layout-content-body.component';
import { LayoutContentTitleComponent } from './components/layout-content-title/layout-content-title.component';
import { LicensesComponent } from './components/licenses/licenses.component';
import { MobileDeviceListComponent } from './components/mobile-device-list/mobile-device-list.component';
import { NxButtonComponent } from './components/nx-button/nx-button.component';
import { PageTitleBoxComponent } from './components/pageTitle-box/pageTitle-box.component';
import { TableHeaderComponent } from './components/table-header/table-header.component';
import { TemplateVariantComponent } from './components/template-variant/template-variant.component';
import { TemplatesComponent } from './components/templates/templates.component';
import { TenantDialogComponent } from './components/tenant-dialog/tenant-dialog.component';
import { TenantListComponent } from './components/tenant-list/tenant-list.component';
import { TenantViewComponent } from './components/tenant-view/tenant-view.component';
import { TenantComponent } from './components/tenant/tenant.component';
import { UserDialogComponent } from './components/user-dialog/user-dialog.component';
import { UserLicenceDialogComponent } from './components/user-licence-dialog/user-licence-dialog.component';
import { UserPasswordDialogComponent } from './components/user-password-dialog/user-password-dialog.component';
import { UserPreferredLanguageDialogComponent } from './components/user-preferred-language-dialog/user-preferred-language-dialog.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { UserViewComponent } from './components/user-view/user-view.component';
import { UserComponent } from './components/user/user.component';
import { UserListComponent } from './components/users-list/user-list.component';
import { NewTabNavigationDirective } from './directives/new-tab-directive';
import { NxTemplateDirective } from './directives/nx-template.directive';
import { EditCalendarInputComponent } from './formControls/edit-calendar-form-control.component';
import { EditCheckboxInputComponent } from './formControls/edit-checkbox-form-control.component';
import { EditChipsInputComponent } from './formControls/edit-chips-form-control.component';
import { EditDateTimePeriodInputV2Component } from './formControls/edit-dueDateTime-form-control-v2.component';
import { EditDateTimePeriodInputComponent } from './formControls/edit-dueDateTime-form-control.component';
import { EditMultiSelectInputComponent } from './formControls/edit-multiSelect-form-control.component';
import { EditNumberInputComponent } from './formControls/edit-number-form-control.component';
import { EditSelectInputComponent } from './formControls/edit-select-form-control.component';
import { EditSelectCountryInputComponent } from './formControls/edit-selectCountry-form-control.component';
import { EditTextInputComponent } from './formControls/edit-text-form-control.component';
import { EditTextfieldInputComponent } from './formControls/edit-textfield-form-control.component';
import { JoinPipe } from './pipes/join.pipe';
import { TranslateLabelsPipe } from './pipes/translation-labels.pipe';
import { ApiKeysService } from './shared/services/apiKeys/apiKeys.service';
import { ApolloService } from './shared/services/apollo.service';
import { ExpandService } from './shared/services/expand.service';
import { FormVariantsService } from './shared/services/formVariants/formVariants.service';
import { GroupsService } from './shared/services/groups/groups.service';
import { GrowthBookService } from './shared/services/growthbook.service';
import { LoadingService } from './shared/services/loading.service';
import { LocationService } from './shared/services/location/location.service';
import { MetricsService } from './shared/services/metrics/metrics.service';
import { PaymentsService } from './shared/services/payment/payment.service';
import { RolesService } from './shared/services/roles/roles.service';
import { ADDITIONAL_COMPLETION_BEHAVIORS, ADDITIONAL_FLOWS, CommandService, COMPLETION_BEHAVIOR_MAP, CompletionBehavior, Flow, FLOW_MAP } from './shared/services/services/commandService/command.service';
import { RedirectCompletionBehavior } from './shared/services/services/commandService/completionBehavior/redirectCompletionBehavior';
import { ReloadCompletionBehavior } from './shared/services/services/commandService/completionBehavior/reloadCompletionBehavior';
import { ToastCompletionBehavior } from './shared/services/services/commandService/completionBehavior/toastCompletionBehavior';
import { ConfirmationDialogFlow } from './shared/services/services/commandService/flows/confirmationDialogFlow';
import { NoFlow } from './shared/services/services/commandService/flows/noFlow';
import { SettingsService } from './shared/services/settings/settings.service';
import { TemplateVariantsService } from './shared/services/templateVariants/templateVariants.service';
import { TenantsService } from './shared/services/tenants/tenants.service';
import { NxToastComponent, ToastService } from './shared/services/toast/toast.service';
import { UsersService } from './shared/services/users/users.service';

const flowMapProvider = {
  provide: FLOW_MAP,
  useFactory: (
    confirmationDialogFlow: ConfirmationDialogFlow,
    noFlow: NoFlow,
    additionalFlows: Map<string, Map<string, Flow>>
  ) => {
    const map = new Map<string, Flow>();
    map.set('confirmationDialogFlow', confirmationDialogFlow);
    map.set('noFlow', noFlow);
    console.log('test');
    // Merge additional flows if provided to one single map
    if (additionalFlows) {
      additionalFlows.forEach((flowMap, key) => {
        flowMap.forEach((flow, flowKey) => {
          map.set(flowKey, flow);
        });
      });
    }


    return map;
  },
  deps: [
    ConfirmationDialogFlow,
    NoFlow,
    [
      new Optional(),
      ADDITIONAL_FLOWS
    ]
  ]
};

const completionBehaviorMapProvider = {
  provide: COMPLETION_BEHAVIOR_MAP,
  useFactory: (
    redirectCompletionBehavior: RedirectCompletionBehavior,
    reloadCompletionBehavior: ReloadCompletionBehavior,
    toastCompletionBehavior: ToastCompletionBehavior,
    additionalCompletionBehaviors: Map<string, CompletionBehavior>
  ) => {
    const map = new Map<string, CompletionBehavior>();
    map.set('redirectCompletionBehavior', redirectCompletionBehavior);
    map.set('reloadCompletionBehavior', reloadCompletionBehavior);
    map.set('toastCompletionBehavior', toastCompletionBehavior);
    // Merge additional completion behaviors if provided
    if (additionalCompletionBehaviors) {
      additionalCompletionBehaviors.forEach((completionBehavior, key) => {
        map.set(key, completionBehavior);
      });
    }
    return map;
  },
  deps: [
    RedirectCompletionBehavior,
    ReloadCompletionBehavior,
    ToastCompletionBehavior,
    [
      new Optional(),
      ADDITIONAL_COMPLETION_BEHAVIORS
    ]
  ]
};

@NgModule({
  declarations: [
    UserListComponent,
    UserComponent,
    TenantListComponent,
    TenantComponent,
    TemplatesComponent,
    TemplateVariantComponent,
    ApiKeysComponent,
    ApiKeyComponent,
    NxButtonComponent,
    LayoutContentTitleComponent,
    LayoutContentBodyComponent,
    ConfirmDialogComponent,
    CommandConfirmDialogComponent,
    HttpLogsComponent,
    GroupViewComponent,
    GroupListComponent,
    DeveloperPageComponent,
    LicensesComponent,
    IncrementDecrementInputComponent,
    InvoicesComponent,
    EditEmailDialogComponent,
    ConfirmEmailDeletionDialogComponent,
    EditInvoiceAddressDialogComponent,
    ConfirmInvoiceAddressDeletionDialogComponent,
    ConfirmLicenceRequestsDeletionDialogComponent,
    FormsListComponent,
    FormVariantComponent,
    PageTitleBoxComponent,
    DefaultButtonComponent,
    CommandButtonComponent,
    NxTemplateDirective,
    HeadlineBoxComponent,
    CompanyViewComponent,
    DefaultBoxComponent,
    TableHeaderComponent,
    EditDialogDynamicContent,
    EditDialogComponent,
    TenantDialogComponent,
    EditTextInputComponent,
    EditChipsInputComponent,
    EditNumberInputComponent,
    EditSelectCountryInputComponent,
    EditCheckboxInputComponent,
    TenantViewComponent,
    EditTextfieldInputComponent,
    CompanyDialogComponent,
    NxConfirmDialogComponent,
    ApiKeyDialogComponent,
    NxToastComponent,
    EmptyBoxComponent,
    UserViewComponent,
    UserProfileComponent,
    UserPasswordDialogComponent,
    EventsComponent,
    MobileDeviceListComponent,
    NxFilterMultiSelect,
    NxFilterMultiSelectItem,
    UserDialogComponent,
    EditMultiSelectInputComponent,
    JoinPipe,
    UserLicenceDialogComponent,
    EditSelectInputComponent,
    NxFilterNumberSelect,
    NxFilterDateSelect,
    AddRolesDialogComponent,
    GroupDialogComponent,
    BillingComponent,
    BillingLicenceViewComponent,
    BillingInvoiceReceiverViewComponent,
    BillingPaymentMethodViewComponent,
    BillingInvoiceListComponent,
    BillingInvoiceReceiverEditEmailDialogComponent,
    BillingInvoiceReceiverEditAddressDialogComponent,
    NewTabNavigationDirective,
    NxFilterDropdown,
    NxFilterDropdownItem,
    EditDateTimePeriodInputComponent,
    EditDateTimePeriodInputV2Component,
    EditCalendarInputComponent,
    UserPreferredLanguageDialogComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ClipboardModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatDialogModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatRadioModule,
    MatSelectModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    NgxMatTimepickerModule,
    MatSelectCountryModule,
    DragDropModule,
    AceModule,
    TableModule,
    TabViewModule,
    MultiSelectModule,
    DropdownModule,
    MenuModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    BlockUIModule,
    InputTextareaModule,
    TagModule,
    OverlayModule,
    SharedModule,
    TooltipModule,
    RippleModule,
    ScrollerModule,
    MultiSelectModule,
    TimesCircleIcon,
    TimesIcon,
    ChevronDownIcon,
    CheckIcon,
    SearchIcon,
    ProgressSpinnerModule,
    CalendarModule,
    TranslateModule.forChild(),
    TranslateLabelsPipe,
    OverlayPanelModule,
    AutoCompleteModule,
    ChipsModule
  ],
   providers: [
    UsersService,
    RolesService,
    LoadingService,
    ApolloService,
    TenantsService,
    TemplateVariantsService,
    ApiKeysService,
    SettingsService,
    StateService,
    GroupsService,
    MetricsService,
    PaymentsService,
    CommandService,
    ExpandService,
    FormVariantsService,
    EditDialogService,
    ConfirmDialogService,
    ToastService,
    LocationService,
    ConfirmationDialogFlow,
    flowMapProvider,
    RedirectCompletionBehavior,
    completionBehaviorMapProvider,
    GrowthBookService
  ],
  exports: [
    UserListComponent,
    UserComponent,
    TenantListComponent,
    TenantComponent,
    TemplatesComponent,
    TemplateVariantComponent,
    ApiKeysComponent,
    ApiKeyComponent,
    ConfirmDialogComponent,
    CommandConfirmDialogComponent,
    NxButtonComponent,
    LayoutContentTitleComponent,
    LayoutContentBodyComponent,
    HttpLogsComponent,
    GroupViewComponent,
    GroupListComponent,
    FormsListComponent,
    FormVariantComponent,
    PageTitleBoxComponent,
    DefaultButtonComponent,
    CommandButtonComponent,
    NxTemplateDirective,
    HeadlineBoxComponent,
    CompanyViewComponent,
    DefaultBoxComponent,
    TableHeaderComponent,
    EditDialogDynamicContent,
    EditDialogComponent,
    EditTextInputComponent,
    EditChipsInputComponent,
    EditNumberInputComponent,
    EditCheckboxInputComponent,
    TenantViewComponent,
    EditTextfieldInputComponent,
    NxConfirmDialogComponent,
    EmptyBoxComponent,
    UserViewComponent,
    UserProfileComponent,
    MobileDeviceListComponent,
    NxFilterMultiSelect,
    NxFilterMultiSelectItem,
    EditMultiSelectInputComponent,
    JoinPipe,
    EditSelectInputComponent,
    NxFilterNumberSelect,
    NxFilterDateSelect,
    BillingComponent,
    NewTabNavigationDirective,
    NxFilterDropdown,
    EditDateTimePeriodInputComponent,
    EditDateTimePeriodInputV2Component,
    EditCalendarInputComponent,
    TranslateModule
  ]
})
export class CoreModuleModule {
  public static forRoot(
    environment: any,
    additionalFlows?: Provider[]
  ): ModuleWithProviders <CoreModuleModule>{
    return {
        ngModule: CoreModuleModule,
        providers: [
            {
                provide: 'env', // you can also use InjectionToken
                useValue: environment
            },
            ...(additionalFlows || [])
        ]
    };
  }
}
